<template>
  <div class="landing-page">
    <!-- ═══════════════ TOP BAR ═══════════════ -->
    <header class="lp-topbar">
      <div class="lp-topbar-inner">
        <div class="lp-brand">
          <div class="lp-brand-mark">L</div>
          <span class="lp-brand-name">LensPoint</span>
        </div>
        <div class="lp-topbar-actions">
          <button class="lp-link-btn" @click="onLoginClick">Log in</button>
          <button class="lp-btn lp-btn--sm" @click="onCtaClick">Sign up</button>
        </div>
      </div>
    </header>

    <!-- ═══════════════ HERO ═══════════════ -->
    <section class="lp-hero">
      <div class="lp-hero-inner">
        <div class="lp-hero-copy">
          <!-- The alpha state is the most useful thing we can lead with; the
               security claims it replaced are still made in the trust bar.
               What alpha means for pricing is spelled out in its own section
               further down, so the badge just states the status. -->
          <div class="lp-badge lp-badge--alpha">
            <i class="mdi mdi-flask-outline" />
            In alpha
          </div>
          <h1 class="lp-headline">
            Your finances,<br />
            <span class="lp-headline-accent">crystal clear.</span>
          </h1>
          <p class="lp-subline">
            LensPoint pulls in your bank transactions automatically, shows you
            exactly where the money goes, and flags when a category runs above
            its usual pace — no budgets to set, no spreadsheets to babysit.
          </p>
          <div class="lp-hero-actions">
            <button class="lp-btn lp-btn--lg" @click="onCtaClick">
              Get started
              <i class="mdi mdi-arrow-right" />
            </button>
            <button class="lp-btn lp-btn--lg lp-btn--ghost" @click="onLoginClick">
              Log in
            </button>
          </div>
          <span class="lp-note">Set up in about a minute · connect a bank and you're going</span>
        </div>

        <!-- Hero mockup: a hand-built composition of the real dashboard cards -->
        <div class="lp-hero-visual" aria-hidden="true">
          <div class="mock-window">
            <div class="mock-titlebar">
              <span class="mock-dot" /><span class="mock-dot" /><span class="mock-dot" />
              <span class="mock-titlebar-label">Overview · June</span>
            </div>
            <div class="mock-body">
              <div class="mock-statrow">
                <div class="mock-stat">
                  <span class="mock-label">Net this month</span>
                  <span class="mock-num mock-num--pos">+$1,204.62</span>
                  <span class="mock-sub">In $6,420 · Out $5,215</span>
                </div>
                <div class="mock-stat">
                  <span class="mock-label">Expected balance</span>
                  <span class="mock-num">$8,668.19</span>
                  <span class="mock-sub">Projected month-end</span>
                </div>
              </div>

              <div class="mock-card">
                <div class="mock-card-h">
                  <span>Where it goes</span>
                  <span class="mock-card-sub">$4,997.30</span>
                </div>
                <div class="mock-bar">
                  <span style="flex: 28; background: var(--lpcat-3)" />
                  <span style="flex: 22; background: var(--lpcat-1)" />
                  <span style="flex: 18; background: var(--lpcat-4)" />
                  <span style="flex: 14; background: var(--lpcat-2)" />
                  <span style="flex: 18; background: var(--ink-4)" />
                </div>
                <div class="mock-legend">
                  <div class="mock-legend-row">
                    <span class="mock-swatch" style="background: var(--lpcat-3)" />
                    <span class="mock-legend-name">Groceries</span>
                    <span class="mock-legend-amt">$1,399.25</span>
                    <span class="mock-legend-pct">28%</span>
                  </div>
                  <div class="mock-legend-row">
                    <span class="mock-swatch" style="background: var(--lpcat-1)" />
                    <span class="mock-legend-name">Restaurants</span>
                    <span class="mock-legend-amt">$1,099.41</span>
                    <span class="mock-legend-pct">22%</span>
                  </div>
                  <div class="mock-legend-row">
                    <span class="mock-swatch" style="background: var(--lpcat-4)" />
                    <span class="mock-legend-name">Transport</span>
                    <span class="mock-legend-amt">$899.51</span>
                    <span class="mock-legend-pct">18%</span>
                  </div>
                  <div class="mock-legend-row">
                    <span class="mock-swatch" style="background: var(--ink-4)" />
                    <span class="mock-legend-name">Other (9 categories)</span>
                    <span class="mock-legend-amt">$899.46</span>
                    <span class="mock-legend-pct">18%</span>
                  </div>
                </div>
              </div>

              <div class="mock-card">
                <div class="mock-card-h">
                  <span>Upcoming</span>
                  <span class="mock-card-sub">Next 14 days</span>
                </div>
                <div class="mock-recur-row">
                  <span class="mock-recur-dot" style="background: var(--lpcat-5)" />
                  <span class="mock-legend-name">Streaming · in 3 days</span>
                  <span class="mock-legend-amt">$15.49</span>
                </div>
                <div class="mock-recur-row">
                  <span class="mock-recur-dot" style="background: var(--lpcat-6)" />
                  <span class="mock-legend-name">Gym · in 6 days</span>
                  <span class="mock-legend-amt">$24.00</span>
                </div>
              </div>
            </div>
          </div>
          <div class="mock-chip">
            <i class="mdi mdi-bank-outline" />
            2 banks synced · just now
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ TRUST BAR ═══════════════ -->
    <section class="lp-trust">
      <div class="lp-trust-inner">
        <div v-for="stat in trustStats" :key="stat.label" class="lp-trust-stat">
          <i :class="['mdi', stat.icon]" />
          <span class="lp-trust-value">{{ stat.value }}</span>
          <span class="lp-trust-label">{{ stat.label }}</span>
        </div>
      </div>
    </section>

    <!-- ═══════════════ FEATURES ═══════════════ -->
    <section class="lp-features" id="features">
      <div class="lp-section-head">
        <h2 class="lp-section-title">Built around how money actually behaves</h2>
        <p class="lp-section-sub">
          Not another budgeting app. LensPoint watches your real spending
          patterns and tells you when something changes.
        </p>
      </div>

      <!-- 1 · Automatic imports -->
      <div class="lp-feature">
        <div class="lp-feature-copy">
          <div class="lp-feature-eyebrow">Automatic imports</div>
          <h3 class="lp-feature-title">Connect once. Every transaction flows in.</h3>
          <p class="lp-feature-desc">
            Link your banks through Plaid and transactions arrive categorized,
            deduplicated, and ready to explore. Custom rules let you
            auto-rename merchants and re-route categories — set them once and
            forget them.
          </p>
          <ul class="lp-feature-list">
            <li><i class="mdi mdi-check" /> 12,000+ supported institutions via Plaid</li>
            <li><i class="mdi mdi-check" /> Rules engine for automatic categorization</li>
            <li><i class="mdi mdi-check" /> Pending charges tracked until they settle</li>
          </ul>
        </div>
        <div class="lp-feature-visual" aria-hidden="true">
          <div class="vig">
            <div class="vig-bank-row">
              <span class="vig-bank-mono" style="background: var(--lpcat-4)">CH</span>
              <div class="vig-col">
                <span class="vig-strong">Chase</span>
                <span class="vig-muted">4 accounts · synced 2 min ago</span>
              </div>
              <i class="mdi mdi-check-circle vig-ok" />
            </div>
            <div class="vig-bank-row">
              <span class="vig-bank-mono" style="background: var(--lpcat-1)">AM</span>
              <div class="vig-col">
                <span class="vig-strong">Amex</span>
                <span class="vig-muted">1 account · synced 2 min ago</span>
              </div>
              <i class="mdi mdi-check-circle vig-ok" />
            </div>
            <div class="vig-rule">
              <i class="mdi mdi-auto-fix" />
              <span>Rule: "AMZN Mktp" → <b>Amazon</b> · Shopping</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 2 · Tracking without budgets (reversed) -->
      <div class="lp-feature lp-feature--rev">
        <div class="lp-feature-copy">
          <div class="lp-feature-eyebrow">Tracking without budgets</div>
          <h3 class="lp-feature-title">No targets to set. No goals to forget.</h3>
          <p class="lp-feature-desc">
            LensPoint automatically tracks the categories you spend the most
            on, using your own trailing three-month average as the baseline.
            When this month's spending rises above that line, you'll see it
            flagged — before the month gets away from you.
          </p>
          <ul class="lp-feature-list">
            <li><i class="mdi mdi-check" /> Baselines adapt as your habits change</li>
            <li><i class="mdi mdi-check" /> Flags categories running hot, ignores noise</li>
            <li><i class="mdi mdi-check" /> Tune or untrack anything in one click</li>
          </ul>
        </div>
        <div class="lp-feature-visual" aria-hidden="true">
          <div class="vig">
            <div class="vig-track-row">
              <div class="vig-col">
                <span class="vig-strong">Groceries</span>
                <span class="vig-muted">$612 of $640/mo usual</span>
              </div>
              <div class="vig-track-bar"><span style="width: 78%; background: var(--lpcat-3)" /></div>
            </div>
            <div class="vig-track-row">
              <div class="vig-col">
                <span class="vig-strong">Restaurants <span class="vig-flag">running hot</span></span>
                <span class="vig-muted">$498 of $410/mo usual</span>
              </div>
              <div class="vig-track-bar"><span style="width: 100%; background: var(--lp-neg)" /></div>
            </div>
            <div class="vig-track-row">
              <div class="vig-col">
                <span class="vig-strong">Transport</span>
                <span class="vig-muted">$214 of $390/mo usual</span>
              </div>
              <div class="vig-track-bar"><span style="width: 46%; background: var(--lpcat-4)" /></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 3 · Household sharing -->
      <div class="lp-feature">
        <div class="lp-feature-copy">
          <div class="lp-feature-eyebrow">Household sharing</div>
          <h3 class="lp-feature-title">Yours, mine, and ours — cleanly separated.</h3>
          <p class="lp-feature-desc">
            Every expense lives in a scope: personal ones stay private,
            household ones are shared with the people you invite. Split a
            single transaction across both, and each member still connects
            their own banks.
          </p>
          <ul class="lp-feature-list">
            <li><i class="mdi mdi-check" /> Invite a partner by email</li>
            <li><i class="mdi mdi-check" /> Split one charge across scopes or categories</li>
            <li><i class="mdi mdi-check" /> Personal data stays personal</li>
          </ul>
        </div>
        <div class="lp-feature-visual" aria-hidden="true">
          <div class="vig">
            <div class="vig-txn">
              <div class="vig-col">
                <span class="vig-strong">Whole Foods</span>
                <span class="vig-muted">Jun 8 · Groceries</span>
              </div>
              <span class="vig-pill vig-pill--hh">Household</span>
              <span class="vig-amt">$86.20</span>
            </div>
            <div class="vig-txn">
              <div class="vig-col">
                <span class="vig-strong">Record store</span>
                <span class="vig-muted">Jun 7 · Hobbies</span>
              </div>
              <span class="vig-pill">Personal</span>
              <span class="vig-amt">$31.99</span>
            </div>
            <div class="vig-split">
              <i class="mdi mdi-call-split" />
              <span>Costco $142.10 → <b>$98 Household</b> + <b>$44.10 Personal</b></span>
            </div>
          </div>
        </div>
      </div>

      <!-- 4 · Plans ahead (reversed) -->
      <div class="lp-feature lp-feature--rev">
        <div class="lp-feature-copy">
          <div class="lp-feature-eyebrow">Looking ahead</div>
          <h3 class="lp-feature-title">Know how the month ends before it does.</h3>
          <p class="lp-feature-desc">
            Recurring charges are detected from your history and projected
            forward, so subscriptions never sneak up on you. Spending and
            income projections show your expected month-end balance from your
            actual running pace.
          </p>
          <ul class="lp-feature-list">
            <li><i class="mdi mdi-check" /> Recurring-charge radar with due dates</li>
            <li><i class="mdi mdi-check" /> Month-end balance projection</li>
            <li><i class="mdi mdi-check" /> Heaviest days, no-spend days, weekend lift</li>
          </ul>
        </div>
        <div class="lp-feature-visual" aria-hidden="true">
          <div class="vig">
            <div class="vig-proj">
              <span class="vig-muted">Projected spending</span>
              <div class="vig-proj-nums">
                <span class="vig-proj-big">$5,180</span>
                <span class="vig-muted">of $6,420 income</span>
              </div>
              <div class="vig-track-bar vig-track-bar--tall">
                <span style="width: 64%; background: var(--ink-2)" />
              </div>
              <div class="vig-proj-foot">
                <span class="vig-muted">Today · day 11 of 30</span>
                <span class="vig-strong" style="color: var(--lp-pos-ink)">+$1,240 expected</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 5 · Runs in the browser -->
      <div class="lp-feature">
        <div class="lp-feature-copy">
          <div class="lp-feature-eyebrow">Every platform</div>
          <h3 class="lp-feature-title">Available everywhere, through your browser.</h3>
          <p class="lp-feature-desc">
            LensPoint is a web app, so there's nothing to download and no app
            store to wait on. On iPhone or Android, add it to your home screen
            and it opens full-screen with its own icon — same as a native app,
            except it's always on the latest version.
          </p>
          <ul class="lp-feature-list">
            <li><i class="mdi mdi-check" /> iPhone, Android, laptop — any modern browser</li>
            <li><i class="mdi mdi-check" /> Pin to your home screen for a full-screen app</li>
            <li><i class="mdi mdi-check" /> Updates land on reload — no store, no downloads</li>
          </ul>
        </div>
        <div class="lp-feature-visual" aria-hidden="true">
          <div class="vig vig--phone">
            <div class="phone">
              <div class="phone-screen">
                <div class="phone-status">
                  <span>9:41</span>
                  <span class="phone-status-glyphs">
                    <i class="mdi mdi-signal-cellular-3" />
                    <i class="mdi mdi-wifi" />
                    <i class="mdi mdi-battery" />
                  </span>
                </div>
                <div class="phone-grid">
                  <!-- The pinned app, sitting on a home screen among placeholders. -->
                  <div class="phone-app phone-app--lp">
                    <span class="phone-icon phone-icon--lp">L</span>
                    <span class="phone-app-name">LensPoint</span>
                  </div>
                  <div v-for="n in 5" :key="n" class="phone-app">
                    <span class="phone-icon" />
                    <span class="phone-app-name phone-app-name--ghost" />
                  </div>
                </div>
                <div class="phone-dock">
                  <span v-for="n in 4" :key="n" class="phone-icon phone-icon--dock" />
                </div>
              </div>
              <span class="phone-bar" />
            </div>
            <div class="vig-rule vig-rule--center">
              <i class="mdi mdi-export-variant" />
              <span>Share → <b>Add to Home Screen</b></span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ HOW IT WORKS ═══════════════ -->
    <section class="lp-how" id="how">
      <div class="lp-section-head">
        <h2 class="lp-section-title">Up and running in minutes</h2>
        <p class="lp-section-sub">Three steps to a clear picture.</p>
      </div>
      <div class="lp-steps">
        <div v-for="(step, i) in steps" :key="step.title" class="lp-step">
          <div class="lp-step-num">{{ i + 1 }}</div>
          <i :class="['mdi', step.icon, 'lp-step-icon']" />
          <h4 class="lp-step-title">{{ step.title }}</h4>
          <p class="lp-step-desc">{{ step.desc }}</p>
        </div>
      </div>
    </section>

    <!-- ═══════════════ ALPHA & SUPPORT ═══════════════ -->
    <section class="lp-alpha" id="pricing">
      <div class="lp-alpha-inner">
        <div class="lp-alpha-copy">
          <div class="lp-feature-eyebrow">Where things stand</div>
          <h2 class="lp-alpha-title">In alpha, and free while it is.</h2>
          <p class="lp-alpha-desc">
            LensPoint is early software. Every feature is open to everyone
            while we build — nothing is held back behind a tier, and there's
            no trial clock running in the background.
          </p>
          <p class="lp-alpha-desc">
            If it earns a place in your week and you'd like to see it keep
            going, you can chip in. Support doesn't unlock anything — except
            our deepest thanks!
          </p>
        </div>
        <div class="lp-alpha-tiers">
          <div class="lp-alpha-tier">
            <span class="lp-alpha-price">$2</span>
            <span class="lp-alpha-per">per month</span>
          </div>
          <span class="lp-alpha-or">or</span>
          <div class="lp-alpha-tier">
            <span class="lp-alpha-price">$20</span>
            <span class="lp-alpha-per">per year</span>
          </div>
          <p class="lp-alpha-fine">
            Optional. Cancel whenever. Set it up in Settings once you're in.
          </p>
        </div>
      </div>
    </section>

    <!-- ═══════════════ FINAL CTA ═══════════════ -->
    <section class="lp-cta">
      <div class="lp-cta-panel">
        <h2 class="lp-cta-title">Ready to see where it all goes?</h2>
        <p class="lp-cta-sub">Every feature, open during alpha. Your banks stay read-only.</p>
        <button class="lp-btn lp-btn--lg lp-btn--inverse" @click="onCtaClick">
          Get started
          <i class="mdi mdi-arrow-right" />
        </button>
      </div>
    </section>

    <!-- ═══════════════ FOOTER ═══════════════ -->
    <footer class="lp-footer">
      <span>&copy; {{ new Date().getFullYear() }} LensPoint Finance</span>
      <router-link to="/privacy" class="lp-footer-link">Privacy Policy</router-link>
      <router-link to="/terms" class="lp-footer-link">Terms of Service</router-link>
      <a href="mailto:info@lenspointfinance.com" class="lp-footer-link">Contact</a>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { globalState } from '@/stores/globalState';

defineOptions({ name: 'Hero' });

const router = useRouter();
const { login, isAuthenticated, isLoading } = useAuth();

// Signed-in visitors don't need marketing — send them to the dashboard once
// the SDK restores the session (PWA relaunches and bookmarked "/" both land
// here). Logout returns to "/" with isAuthenticated false, so no loop.
watch(
  [isLoading, isAuthenticated],
  ([loading, authed]) => {
    if (!loading && authed) router.replace('/overview');
  },
  { immediate: true },
);

const onCtaClick = async () => {
  if (globalState.isAuthenticated) {
    router.push('/overview');
    return;
  }
  await login({ mode: 'signup', redirectTo: '/onboarding' });
};

const onLoginClick = async () => {
  if (globalState.isAuthenticated) {
    router.push('/overview');
    return;
  }
  await login({ mode: 'login', redirectTo: '/overview' });
};

const trustStats = [
  { icon: 'mdi-bank-outline',        value: '12,000+',   label: 'Banks supported via Plaid' },
  { icon: 'mdi-cash-lock',           value: 'Read-only', label: 'We can never move money' },
  { icon: 'mdi-shield-lock-outline', value: '256-bit',   label: 'Encryption in transit & at rest' },
  { icon: 'mdi-account-group-outline', value: 'Built-in', label: 'Household sharing & splits' },
];

const steps = [
  {
    icon: 'mdi-account-plus-outline',
    title: 'Create an account',
    desc: 'Sign up in seconds. Free while we’re in alpha.',
  },
  {
    icon: 'mdi-link-variant',
    title: 'Connect your banks',
    desc: 'Link the accounts you want to follow through Plaid, and invite your household.',
  },
  {
    icon: 'mdi-chart-timeline-variant-shimmer',
    title: 'Watch it come into focus',
    desc: 'Breakdowns, baselines, and projections appear as your history builds.',
  },
];
</script>

<style scoped>
/* =========================================================
   Landing page — theme-independent. The app stores the
   user's light/dark preference globally; marketing must not
   inherit it, so every token the page consumes is pinned to
   the light palette here. No Vuetify components are used.
   ========================================================= */
.landing-page {
  /* Pinned light tokens (mirror tokens.scss :root values) */
  --bg:            #f6f6f4;
  --bg-elev:       #ffffff;
  --surface:       #ffffff;
  --surface-2:     #f1f1ee;
  --surface-3:     #e8e8e3;
  --border:        #e6e5e0;
  --border-strong: #d2d1ca;
  --ink:           #131312;
  --ink-2:         #34332f;
  --ink-3:         #6a6963;
  --ink-4:         #9c9a92;
  --shadow-sm: 0 1px 2px rgba(20, 18, 10, 0.04);
  --shadow:    0 2px 8px rgba(20, 18, 10, 0.05), 0 1px 2px rgba(20, 18, 10, 0.04);

  /* Landing-local accents (light category palette) */
  --lpcat-1: oklch(0.65 0.12 30);
  --lpcat-2: oklch(0.70 0.10 80);
  --lpcat-3: oklch(0.68 0.11 145);
  --lpcat-4: oklch(0.62 0.11 220);
  --lpcat-5: oklch(0.55 0.12 310);
  --lpcat-6: oklch(0.66 0.08 195);
  --lp-pos-ink: oklch(0.38 0.10 150);
  --lp-neg: oklch(0.50 0.14 25);

  --lp-max: 1120px;

  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  overflow-x: hidden;
  font-family: var(--font-sans);
}

/* ---- Buttons ---- */
.lp-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  font: inherit;
  font-size: 13.5px;
  font-weight: 550;
  letter-spacing: -0.005em;
  background: var(--ink);
  color: var(--bg);
  border: 1px solid var(--ink);
  border-radius: var(--radius-md);
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--transition-fast), transform var(--transition-fast);
}
.lp-btn:hover { background: #34332f; }
.lp-btn:active { transform: translateY(1px); }
.lp-btn--sm { padding: 7px 14px; font-size: 13px; }
.lp-btn--lg { padding: 13px 26px; font-size: 14.5px; }
.lp-btn--ghost {
  background: transparent;
  color: var(--ink-2);
  border-color: var(--border-strong);
}
.lp-btn--ghost:hover { background: var(--surface-2); color: var(--ink); }
.lp-btn--inverse {
  background: var(--bg);
  color: var(--ink);
  border-color: var(--bg);
}
.lp-btn--inverse:hover { background: #ffffff; }
.lp-btn .mdi { font-size: 16px; }

/* ---- Top bar ---- */
.lp-topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(246, 246, 244, 0.88);
  /* Prefixed first: the minifier keeps only the last of the pair, and the
     unprefixed one is the one Firefox reads. */
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border);
}
.lp-topbar-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 28px;
}
.lp-brand { display: flex; align-items: center; gap: 10px; }
.lp-brand-mark {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: var(--ink);
  color: var(--bg);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
}
.lp-brand-name { font-weight: 600; font-size: 15px; letter-spacing: -0.015em; }
.lp-topbar-actions { display: flex; align-items: center; gap: 10px; margin-left: auto; }
.lp-link-btn {
  background: none;
  border: 0;
  padding: 6px 10px;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--ink-2);
  cursor: pointer;
  border-radius: 6px;
  transition: color var(--transition-fast), background var(--transition-fast);
}
.lp-link-btn:hover { color: var(--ink); background: var(--surface-2); }

/* ---- Hero ---- */
.lp-hero { padding: 72px 24px 64px; }
.lp-hero-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  gap: 56px;
  align-items: center;
}
.lp-hero-copy { display: flex; flex-direction: column; gap: 22px; }
.lp-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 13px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 550;
  color: var(--ink-2);
  background: var(--surface);
  border: 1px solid var(--border);
  width: fit-content;
}
.lp-badge .mdi { font-size: 14px; color: var(--lp-pos-ink); }
/* Alpha badge reads as a status, not a reassurance — hence the amber. */
.lp-badge--alpha { border-color: var(--border-strong); }
.lp-badge--alpha .mdi { color: oklch(0.55 0.13 75); }
.lp-headline {
  font-size: clamp(2.3rem, 4.5vw, 3.5rem);
  font-weight: 650;
  line-height: 1.08;
  letter-spacing: -0.03em;
  margin: 0;
}
.lp-headline-accent { color: var(--ink-3); font-weight: 500; }
.lp-subline {
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--ink-3);
  max-width: 460px;
  margin: 0;
}
.lp-hero-actions { display: flex; flex-wrap: wrap; gap: 12px; }
.lp-note { font-size: 12px; color: var(--ink-4); }

/* ---- Hero mockup ---- */
.lp-hero-visual { position: relative; }
.mock-window {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 24px 60px rgba(20, 18, 10, 0.10), 0 4px 16px rgba(20, 18, 10, 0.05);
  overflow: hidden;
}
.mock-titlebar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-2);
}
.mock-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--border-strong);
}
.mock-titlebar-label {
  margin-left: 8px;
  font-size: 11px;
  font-weight: 550;
  color: var(--ink-4);
}
.mock-body { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.mock-statrow { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.mock-stat {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  background: var(--surface-2);
  border-radius: var(--radius);
}
.mock-label {
  font-size: 10.5px;
  font-weight: 550;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-4);
}
.mock-num {
  font-family: var(--font-mono);
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.02em;
}
.mock-num--pos { color: var(--lp-pos-ink); }
.mock-sub { font-size: 11px; color: var(--ink-3); }
.mock-card {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px 14px;
}
.mock-card-h {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 12.5px;
  font-weight: 600;
  margin-bottom: 10px;
}
.mock-card-sub { font-size: 11px; font-weight: 500; color: var(--ink-4); }
.mock-bar {
  display: flex;
  gap: 2px;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 10px;
}
.mock-bar span { min-width: 3px; }
.mock-legend { display: flex; flex-direction: column; gap: 6px; }
.mock-legend-row, .mock-recur-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.mock-recur-row { padding: 4px 0; }
.mock-swatch { width: 9px; height: 9px; border-radius: 3px; flex-shrink: 0; }
.mock-recur-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.mock-legend-name { flex: 1; color: var(--ink-2); }
.mock-legend-amt { font-family: var(--font-mono); font-size: 11.5px; }
.mock-legend-pct {
  width: 32px;
  text-align: right;
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--ink-4);
}
.mock-chip {
  position: absolute;
  bottom: -14px;
  left: 28px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 550;
  background: var(--ink);
  color: var(--bg);
  border-radius: 999px;
  box-shadow: var(--shadow);
}
.mock-chip .mdi { font-size: 14px; }

/* ---- Trust bar ---- */
.lp-trust {
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  padding: 26px 24px;
}
.lp-trust-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}
.lp-trust-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2px;
}
.lp-trust-stat .mdi { font-size: 22px; color: var(--ink-3); margin-bottom: 4px; }
.lp-trust-value { font-size: 15px; font-weight: 650; letter-spacing: -0.01em; }
.lp-trust-label { font-size: 11.5px; color: var(--ink-3); }

/* ---- Features ---- */
.lp-features {
  max-width: var(--lp-max);
  margin: 0 auto;
  padding: 88px 24px 48px;
}
.lp-section-head { text-align: center; margin-bottom: 72px; }
.lp-section-title {
  font-size: clamp(1.7rem, 3vw, 2.3rem);
  font-weight: 650;
  letter-spacing: -0.025em;
  margin: 0 0 12px;
}
.lp-section-sub {
  font-size: 15px;
  color: var(--ink-3);
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.6;
}
.lp-feature {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  gap: 56px;
  align-items: center;
  margin-bottom: 88px;
}
.lp-feature:last-child { margin-bottom: 0; }
.lp-feature--rev { direction: rtl; }
.lp-feature--rev > * { direction: ltr; }
.lp-feature-copy { display: flex; flex-direction: column; gap: 14px; }
.lp-feature-eyebrow {
  font-size: 11px;
  font-weight: 550;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.lp-feature-title {
  font-size: 1.45rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0;
}
.lp-feature-desc {
  font-size: 14px;
  line-height: 1.65;
  color: var(--ink-3);
  margin: 0;
  max-width: 440px;
}
.lp-feature-list {
  list-style: none;
  padding: 0;
  margin: 2px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lp-feature-list li {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  color: var(--ink-2);
}
.lp-feature-list .mdi { font-size: 15px; color: var(--lp-pos-ink); }

/* ---- Feature vignettes ---- */
.vig {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 12px 36px rgba(20, 18, 10, 0.07), 0 2px 10px rgba(20, 18, 10, 0.04);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.vig-col { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.vig-strong { font-size: 13px; font-weight: 550; }
.vig-muted { font-size: 11.5px; color: var(--ink-3); }
.vig-amt { font-family: var(--font-mono); font-size: 12.5px; flex-shrink: 0; }

.vig-bank-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: var(--surface-2);
  border-radius: var(--radius);
}
.vig-bank-mono {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.vig-ok { font-size: 18px; color: var(--lp-pos-ink); }
.vig-rule, .vig-split {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  font-size: 12px;
  color: var(--ink-2);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius);
}
.vig-rule .mdi, .vig-split .mdi { font-size: 15px; color: var(--ink-3); }

.vig-track-row { display: flex; flex-direction: column; gap: 7px; padding: 4px 2px; }
.vig-track-bar {
  height: 7px;
  border-radius: 999px;
  background: var(--surface-3);
  overflow: hidden;
  display: flex;
}
.vig-track-bar--tall { height: 10px; }
.vig-track-bar span { border-radius: 999px; }
.vig-flag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  font-size: 10px;
  font-weight: 600;
  border-radius: 999px;
  background: oklch(0.95 0.03 25);
  color: var(--lp-neg);
  vertical-align: 1px;
}

.vig-txn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: var(--surface-2);
  border-radius: var(--radius);
}
.vig-pill {
  padding: 2px 9px;
  font-size: 10.5px;
  font-weight: 600;
  border-radius: 999px;
  background: var(--surface-3);
  color: var(--ink-2);
  flex-shrink: 0;
}
.vig-pill--hh { background: oklch(0.93 0.04 310); color: oklch(0.45 0.12 310); }

/* ---- Phone mock (Every platform) ----
   A CSS-only handset: bezel, screen, and a home screen with the pinned app
   among placeholders. No image asset, so it themes with the page and stays
   sharp at any density. */
.vig--phone { align-items: center; gap: 16px; }
.phone {
  width: 172px;
  aspect-ratio: 9 / 18.5;
  padding: 6px;
  border-radius: 26px;
  background: var(--ink);
  box-shadow: 0 12px 30px rgba(20, 18, 10, 0.22);
  position: relative;
  flex-shrink: 0;
}
.phone-screen {
  height: 100%;
  border-radius: 21px;
  overflow: hidden;
  background: linear-gradient(165deg, var(--surface-3), var(--surface));
  display: flex;
  flex-direction: column;
  padding: 9px 10px;
}
.phone-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 8.5px;
  font-weight: 600;
  color: var(--ink-2);
  padding: 1px 2px 12px;
}
.phone-status-glyphs { display: flex; gap: 2px; }
.phone-status-glyphs .mdi { font-size: 9px; }
/* 3-up icon grid, the app-store-free home screen. */
.phone-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 10px;
}
.phone-app { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.phone-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--surface-3);
  display: block;
}
/* The real one: brand ink + the wordmark's initial, matching .lp-brand-mark. */
.phone-icon--lp {
  background: var(--ink);
  color: var(--bg);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 16px;
  box-shadow: 0 3px 8px rgba(20, 18, 10, 0.28);
}
.phone-app-name { font-size: 7.5px; font-weight: 550; color: var(--ink-2); }
.phone-app-name--ghost {
  width: 26px;
  height: 5px;
  border-radius: 3px;
  background: var(--surface-3);
}
/* Dock: pinned to the bottom so the screen doesn't read as half-empty, and it
   sells "home screen" faster than another row of icons would. */
.phone-dock {
  margin-top: auto;
  margin-bottom: 14px;
  display: flex;
  justify-content: space-around;
  gap: 6px;
  padding: 7px 8px;
  border-radius: 14px;
  background: color-mix(in oklab, var(--ink) 7%, transparent);
}
.phone-icon--dock { width: 27px; height: 27px; border-radius: 7px; }

/* Home indicator. */
.phone-bar {
  position: absolute;
  bottom: 11px;
  left: 50%;
  transform: translateX(-50%);
  width: 54px;
  height: 3px;
  border-radius: 999px;
  background: var(--ink-4);
  opacity: 0.75;
}
.vig-rule--center { justify-content: center; align-self: stretch; }

.vig-proj { display: flex; flex-direction: column; gap: 10px; padding: 6px 4px; }
.vig-proj-nums { display: flex; align-items: baseline; gap: 10px; }
.vig-proj-big {
  font-family: var(--font-mono);
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
}
.vig-proj-foot { display: flex; justify-content: space-between; align-items: center; }

/* ---- How it works ---- */
.lp-how {
  background: var(--surface);
  border-top: 1px solid var(--border);
  padding: 80px 24px;
}
.lp-steps {
  max-width: var(--lp-max);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}
.lp-step {
  position: relative;
  text-align: center;
  padding: 34px 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.lp-step-num {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--surface-2);
  border: 1px solid var(--border);
  color: var(--ink-2);
  font-weight: 650;
  font-size: 12px;
  display: grid;
  place-items: center;
}
.lp-step-icon { font-size: 30px; color: var(--ink-2); margin-bottom: 12px; }
.lp-step-title { font-size: 15px; font-weight: 650; margin: 0 0 7px; letter-spacing: -0.01em; }
.lp-step-desc {
  font-size: 13px;
  color: var(--ink-3);
  line-height: 1.6;
  margin: 0;
  max-width: 260px;
}

/* ---- Alpha & support ---- */
.lp-alpha { padding: 80px 24px 8px; }
.lp-alpha-inner {
  max-width: var(--lp-max);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
  gap: 56px;
  align-items: center;
}
.lp-alpha-copy { display: flex; flex-direction: column; gap: 14px; }
.lp-alpha-title {
  font-size: clamp(1.6rem, 2.8vw, 2.1rem);
  font-weight: 650;
  letter-spacing: -0.025em;
  line-height: 1.15;
  margin: 0;
}
.lp-alpha-desc {
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--ink-3);
  margin: 0;
  max-width: 480px;
}
/* Prices sit in their own panel: the two amounts are the point of the
   section, so they get the visual weight rather than the paragraph. */
.lp-alpha-tiers {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 18px;
  padding: 30px 26px 24px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 12px 36px rgba(20, 18, 10, 0.07), 0 2px 10px rgba(20, 18, 10, 0.04);
  text-align: center;
}
.lp-alpha-tier { display: flex; flex-direction: column; gap: 2px; }
.lp-alpha-price {
  font-size: 34px;
  font-weight: 650;
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.lp-alpha-per { font-size: 12px; color: var(--ink-3); }
.lp-alpha-or { font-size: 12px; color: var(--ink-4); }
.lp-alpha-fine {
  grid-column: 1 / -1;
  margin: 6px 0 0;
  padding-top: 16px;
  border-top: 1px solid var(--border);
  font-size: 12px;
  line-height: 1.55;
  color: var(--ink-4);
}

/* ---- Final CTA ---- */
.lp-cta { padding: 88px 24px; }
.lp-cta-panel {
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
  background: var(--ink);
  color: var(--bg);
  border-radius: var(--radius-xl);
  padding: 56px 32px;
}
.lp-cta-title {
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: 650;
  letter-spacing: -0.02em;
  margin: 0 0 10px;
  color: var(--bg);
}
.lp-cta-sub { font-size: 14px; color: #9c9a92; margin: 0 0 26px; }

/* ---- Footer ---- */
.lp-footer {
  border-top: 1px solid var(--border);
  padding: 22px 24px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 24px;
  font-size: 12.5px;
  color: var(--ink-3);
}
.lp-footer-link {
  color: var(--ink-2);
  font-weight: 500;
  transition: color var(--transition-fast);
}
.lp-footer-link:hover { color: var(--ink); text-decoration: underline; }

/* ---- Responsive ---- */
@media (max-width: 900px) {
  .lp-hero { padding: 44px 20px 56px; }
  .lp-hero-inner { grid-template-columns: 1fr; gap: 44px; }
  .lp-hero-copy { align-items: center; text-align: center; }
  .lp-hero-actions { justify-content: center; }
  .lp-trust-inner { grid-template-columns: repeat(2, 1fr); gap: 24px 12px; }
  .lp-feature,
  .lp-feature--rev { grid-template-columns: 1fr; gap: 28px; margin-bottom: 64px; }
  .lp-feature-copy { align-items: center; text-align: center; }
  .lp-feature-list { align-items: flex-start; }
  .lp-steps { grid-template-columns: 1fr; gap: 40px; }
  .lp-section-head { margin-bottom: 48px; }
  .lp-alpha { padding-top: 56px; }
  .lp-alpha-inner { grid-template-columns: 1fr; gap: 32px; }
  .lp-alpha-copy { align-items: center; text-align: center; }
}
</style>
